import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSEO } from "@/hooks/useSEO";
import { CTALink } from "@/components/common/CTALink";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading } from "@/components/common/SectionHeading";
import { FeatureCard } from "@/components/blocks/FeatureCard";
import { FAQ } from "@/components/blocks/FAQ";
import { CtaBand } from "@/components/blocks/CtaBand";
import { HeroVisual } from "@/components/visuals/HeroVisual";
import { FlowDiagram } from "@/components/visuals/FlowDiagram";
import { CampaignDashboard } from "@/components/visuals/CampaignDashboard";
import { TelephonyDashboard } from "@/components/visuals/TelephonyDashboard";
import { SOLUTIONS, INDUSTRIES, HOME_FAQS } from "@/data/site";

const TRUST = ["Businesses", "Startups", "Enterprises", "Agencies", "Education", "Healthcare", "Real Estate", "E-commerce"];

const WHY = [
  { icon: "Layers", title: "Multi-Channel Communication", desc: "Connect with customers through WhatsApp, SMS and voice from one platform." },
  { icon: "Workflow", title: "Automation First", desc: "Reduce repetitive communication tasks through smart automation." },
  { icon: "Server", title: "Scalable Infrastructure", desc: "Designed to support businesses as communication volumes grow." },
  { icon: "LineChart", title: "Real-Time Insights", desc: "Track campaigns, messages and calls through reporting and analytics." },
  { icon: "Target", title: "Business-Focused Solutions", desc: "Built around sales, marketing, support and operations." },
  { icon: "Headphones", title: "Dedicated Support", desc: "Responsive assistance for onboarding and ongoing usage." },
];

const STEPS = [
  { n: "01", title: "Tell Us Your Requirement", desc: "We understand the communication requirements of your business.", icon: "ClipboardList" },
  { n: "02", title: "Choose Your Solution", desc: "Select WhatsApp, SMS, Telephony or a combination.", icon: "MousePointerClick" },
  { n: "03", title: "Setup & Integrate", desc: "Configure the required communication infrastructure and integrations.", icon: "Settings2" },
  { n: "04", title: "Launch & Automate", desc: "Start communicating with customers and automate repetitive workflows.", icon: "Rocket" },
];

const USE_CASES = [
  { icon: "UserCheck", title: "Lead Follow-Up", desc: "Automatically follow up with prospects." },
  { icon: "BellRing", title: "Customer Notifications", desc: "Send updates, alerts and important information." },
  { icon: "Megaphone", title: "Marketing Campaigns", desc: "Reach customers with promotional campaigns." },
  { icon: "CalendarClock", title: "Appointment Reminders", desc: "Send automated reminders via WhatsApp/SMS." },
  { icon: "Handshake", title: "Sales Communication", desc: "Improve communication between sales teams and prospects." },
  { icon: "LifeBuoy", title: "Customer Support", desc: "Create automated customer communication workflows." },
  { icon: "KeyRound", title: "OTP & Verification", desc: "Send authentication and verification messages." },
  { icon: "PhoneCall", title: "Business Calling", desc: "Manage inbound and outbound calls through smart telephony." },
];

const INTEGRATIONS = ["CRM", "Website", "E-commerce", "ERP", "Lead Management", "APIs", "Webhooks", "Business Apps"];
const INTEGRATION_ICONS = { CRM: "Contact", Website: "Globe", "E-commerce": "ShoppingBag", ERP: "Boxes", "Lead Management": "Users", APIs: "Code2", Webhooks: "Webhook", "Business Apps": "AppWindow" };

const PLANS = [
  { name: "Starter", for: "For small businesses", features: ["Single communication channel", "Essential automation", "Standard delivery reporting", "Email support"], popular: false },
  { name: "Growth", for: "For growing businesses", features: ["Multiple channels (WhatsApp + SMS)", "Advanced automation workflows", "Campaign analytics", "Priority support", "API & webhook access"], popular: true },
  { name: "Enterprise", for: "For larger communication needs", features: ["WhatsApp + SMS + Telephony", "Custom automation & routing", "Dedicated onboarding", "SLA-backed support", "Custom integrations"], popular: false },
];

export default function Home() {
  useSEO({
    title: "E Telecom — WhatsApp Automation, Bulk SMS & Smart Telephony in India",
    description:
      "E Telecom powers smarter business communication with WhatsApp automation, bulk WhatsApp campaigns, bulk SMS and smart telephony. One platform to connect, engage and automate.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "E Telecom",
      url: "https://etelecom.in",
      description: "Business communication and automation solutions — WhatsApp automation, bulk WhatsApp campaigning, bulk SMS and smart telephony.",
      areaServed: "IN",
    },
  });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden et-hero-wash" data-testid="home-hero">
        <div className="absolute inset-0 et-dot-grid opacity-[0.4] pointer-events-none" />
        <div className="et-container relative py-16 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
            <div>
              <Reveal>
                <span className="et-eyebrow"><Icons.Sparkles className="h-3.5 w-3.5" /> Powering Smarter Business Communication</span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mt-5 leading-[1.05]">
                  Smarter Communication.<br />
                  <span className="text-[var(--et-primary)]">Powerful Automation.</span><br />
                  Better Business.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                  E Telecom helps businesses communicate with customers at scale through WhatsApp automation, bulk WhatsApp campaigns, bulk SMS and intelligent telephony solutions.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <CTALink to="/pricing" variant="primary" data-testid="home-hero-primary-cta" withArrow>Get Started</CTALink>
                  <CTALink to="/#solutions" variant="secondary" data-testid="home-hero-secondary-cta">Explore Solutions</CTALink>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1.5"><Icons.MessageSquareText className="h-4 w-4 text-[var(--et-primary)]" /> WhatsApp</span>
                  <span className="inline-flex items-center gap-1.5"><Icons.MessageCircle className="h-4 w-4 text-[var(--et-primary)]" /> SMS</span>
                  <span className="inline-flex items-center gap-1.5"><Icons.PhoneCall className="h-4 w-4 text-[var(--et-primary)]" /> Smart Telephony</span>
                  <span className="inline-flex items-center gap-1.5"><Icons.Workflow className="h-4 w-4 text-[var(--et-primary)]" /> Automation</span>
                </div>
              </Reveal>
            </div>
            <div className="lg:pl-6">
              <HeroVisual />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-slate-200 bg-white py-8">
        <div className="et-container">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400">Built for Businesses That Communicate at Scale</p>
          <div className="mt-6 relative overflow-hidden">
            <div className="flex w-max et-marquee gap-3">
              {[...TRUST, ...TRUST].map((t, i) => (
                <span key={i} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600">
                  <Icons.CircleCheck className="h-4 w-4 text-[var(--et-primary)]" /> {t}
                </span>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent" />
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section id="solutions" className="et-section" data-testid="home-solutions">
        <div className="et-container">
          <SectionHeading
            eyebrow="Our Solutions"
            title="One Platform. Multiple Ways to Connect."
            subtitle="Everything your business needs to reach customers, automate communication and manage conversations more efficiently."
          />
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {SOLUTIONS.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Sparkles;
              return (
                <Reveal key={s.slug} delay={i * 0.05} className="h-full">
                  <div className="et-card et-card-hover h-full p-7 flex flex-col">
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgba(37,99,235,0.1)] text-[var(--et-primary)]"><Icon className="h-6 w-6" /></span>
                      <h3 className="font-display text-xl font-semibold text-slate-900">{s.title}</h3>
                    </div>
                    <p className="mt-4 text-sm text-slate-600 leading-relaxed">{s.short}</p>
                    <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5">
                      {s.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                          <Icons.Check className="h-4 w-4 text-[var(--et-primary)] mt-0.5 shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Link to={s.path} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--et-primary)] hover:gap-2.5 transition-all">
                      {s.cta} <Icons.ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-6">
            <p className="text-center text-xs text-slate-500 max-w-2xl mx-auto">
              WhatsApp campaigns should be sent only to recipients in accordance with applicable laws, platform policies and consent requirements. E Telecom promotes responsible, permission-based communication.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY E TELECOM */}
      <section className="et-section bg-white" data-testid="home-why-et">
        <div className="et-container">
          <SectionHeading eyebrow="Why E Telecom" title="Communication Technology Built Around Your Business" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <FeatureCard key={w.title} icon={w.icon} title={w.title} description={w.desc} delay={i * 0.04} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="et-section" data-testid="home-how-it-works">
        <div className="et-container">
          <SectionHeading eyebrow="How It Works" title="From Requirement to Automation in Four Steps" />
          <div className="mt-12" data-testid="how-it-works-timeline">
            <div className="grid lg:grid-cols-4 gap-6 lg:gap-4 relative">
              <div className="hidden lg:block absolute top-[34px] left-[12%] right-[12%] h-px bg-gradient-to-r from-[rgba(37,99,235,0.15)] via-[rgba(37,99,235,0.35)] to-[rgba(37,99,235,0.15)]" />
              {STEPS.map((s, i) => {
                const Icon = Icons[s.icon] || Icons.Circle;
                return (
                  <Reveal key={s.n} delay={i * 0.08} className="relative">
                    <div className="flex flex-col items-center text-center">
                      <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl bg-white border border-slate-200 shadow-sm text-[var(--et-primary)]">
                        <Icon className="h-6 w-6" />
                        <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-[var(--et-navy)] text-[10px] font-bold text-[var(--et-accent)]">{s.n}</span>
                      </span>
                      <h3 className="font-display text-base font-semibold text-slate-900 mt-5">{s.title}</h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-[240px]">{s.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="et-section bg-white">
        <div className="et-container">
          <SectionHeading eyebrow="Use Cases" title="What Can You Automate?" subtitle="Practical ways businesses use E Telecom to communicate faster and reduce manual work." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {USE_CASES.map((u, i) => {
              const Icon = Icons[u.icon] || Icons.Sparkles;
              return (
                <Reveal key={u.title} delay={(i % 4) * 0.05}>
                  <div className="group rounded-2xl border border-slate-200/70 p-5 h-full transition-colors hover:border-[rgba(37,99,235,0.3)] hover:bg-[rgba(37,99,235,0.02)]">
                    <span className="et-icon-box"><Icon className="h-5 w-5" /></span>
                    <h3 className="font-display text-base font-semibold text-slate-900 mt-4">{u.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{u.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" className="et-section" data-testid="home-industries">
        <div className="et-container">
          <SectionHeading eyebrow="Industries" title="Solutions for Every Customer Conversation" subtitle="Trusted communication workflows tailored to how different industries reach and serve their customers." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INDUSTRIES.map((ind, i) => {
              const Icon = Icons[ind.icon] || Icons.Building2;
              return (
                <Reveal key={ind.name} delay={(i % 3) * 0.05}>
                  <div className="et-card et-card-hover h-full p-6">
                    <div className="flex items-center gap-3">
                      <span className="et-icon-box"><Icon className="h-5 w-5" /></span>
                      <h3 className="font-display text-base font-semibold text-slate-900">{ind.name}</h3>
                    </div>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{ind.use}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMBINED COMMUNICATION */}
      <section className="et-section bg-white" data-testid="home-combined-flow">
        <div className="et-container">
          <SectionHeading eyebrow="One Strategy" title="WhatsApp + SMS + Voice. One Communication Strategy." subtitle="Combine channels according to your communication requirements — orchestrated through automation and connected to your business systems." />
          <div className="mt-12"><FlowDiagram /></div>
        </div>
      </section>

      {/* DASHBOARD PREVIEW */}
      <section className="et-section" data-testid="home-dashboard-preview">
        <div className="et-container">
          <SectionHeading eyebrow="Dashboard Preview" title="See Your Communication at a Glance" subtitle="A clear view of campaigns, delivery and calling activity. The figures below are illustrative UI demonstrations." />
          <Reveal className="mt-10 max-w-4xl mx-auto">
            <Tabs defaultValue="campaign" className="w-full">
              <TabsList className="mx-auto grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="campaign" data-testid="dashboard-tab-campaign">Campaign Analytics</TabsTrigger>
                <TabsTrigger value="telephony" data-testid="dashboard-tab-telephony">Telephony Analytics</TabsTrigger>
              </TabsList>
              <TabsContent value="campaign" className="mt-6"><CampaignDashboard /></TabsContent>
              <TabsContent value="telephony" className="mt-6"><TelephonyDashboard /></TabsContent>
            </Tabs>
          </Reveal>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="et-section bg-white">
        <div className="et-container">
          <SectionHeading eyebrow="Integrations" title="Connect E Telecom With Your Existing Business Tools" subtitle="Bring communication into the tools your teams already use. Final integration availability depends on the specific system and requirements." />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {INTEGRATIONS.map((name, i) => {
              const Icon = Icons[INTEGRATION_ICONS[name]] || Icons.Puzzle;
              return (
                <Reveal key={name} delay={(i % 4) * 0.05}>
                  <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200/70 bg-white p-6 text-center transition-all hover:border-[rgba(37,99,235,0.3)] hover:shadow-sm">
                    <span className="et-icon-box"><Icon className="h-5 w-5" /></span>
                    <span className="text-sm font-medium text-slate-700">{name}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-8 text-center">
            <CTALink to="/contact" variant="secondary">Talk to Our Integration Team</CTALink>
          </Reveal>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="et-section" data-testid="home-pricing-preview">
        <div className="et-container">
          <SectionHeading eyebrow="Pricing" title="Flexible Plans for Every Business" subtitle="Plans are shaped around your communication requirements. Get custom pricing based on channels, volume and automation needs." />
          <div className="mt-12 grid lg:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06} className="h-full">
                <div className={`relative h-full rounded-2xl p-7 flex flex-col ${p.popular ? "bg-[var(--et-navy)] text-white shadow-[0_20px_60px_rgba(2,6,23,0.25)]" : "et-card"}`}>
                  {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--et-accent)] px-3 py-1 text-[11px] font-bold text-slate-900">Most Popular</span>}
                  <h3 className={`font-display text-xl font-bold ${p.popular ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                  <p className={`mt-1 text-sm ${p.popular ? "text-slate-300" : "text-slate-500"}`}>{p.for}</p>
                  <div className={`mt-5 font-display text-2xl font-bold ${p.popular ? "text-[var(--et-accent)]" : "text-[var(--et-primary)]"}`}>Get Custom Pricing</div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2 text-sm ${p.popular ? "text-slate-200" : "text-slate-600"}`}>
                        <Icons.Check className={`h-4 w-4 mt-0.5 shrink-0 ${p.popular ? "text-[var(--et-accent)]" : "text-[var(--et-primary)]"}`} /> {f}
                      </li>
                    ))}
                  </ul>
                  <CTALink to="/pricing" variant={p.popular ? "accent" : "primary"} className="mt-7 w-full" withArrow>Request a Quote</CTALink>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FAQ items={HOME_FAQS} subtitle="Answers to common questions about E Telecom's communication and automation solutions." />

      <CtaBand />
    </>
  );
}
